/**
 * Campaign Audience Filter Controller
 *
 * This controller solves the issue of modal filters affecting the main page filters.
 *
 * PROBLEM:
 * When using filters within a modal (like selecting leads to add to a campaign),
 * the filter events were bubbling up the DOM tree and being captured by the main page's
 * filter controllers. This caused:
 *   1. Full page reloads when modal filters were changed
 *   2. Modal state being lost during these reloads
 *   3. Modal filters unintentionally affecting the main page filters
 *
 * SOLUTION:
 * This controller:
 *   1. Intercepts all filter events from modal components
 *   2. Stops event propagation to prevent bubbling to the main page
 *   3. Updates filter state in the audience controller
 *   4. Triggers sample refresh within the modal only
 *
 * HOW IT WORKS:
 * We create a context-aware boundary between modal and main page:
 *   - All filter controllers have been updated to include a "context" value
 *   - Modal filters use "modal" context, main page filters use "main" context (or no context for fallback)
 *   - This controller only processes events with matching context
 *   - Event propagation is stopped to isolate modal filtering
 *
 * MAINTENANCE NOTES:
 * - If adding new filter types, add corresponding event listeners here
 * - Each filter controller must include context awareness in its dispatchFilterEvent method
 * - The audience controller must have a "filters" property and a displayLeadsSample method
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get targets() {
    return []
  }

  static get values() {
    return {
      context: { type: String, default: "modal" }
    }
  }

  // We need to intercept these specific events before they bubble up to the main page filters.
  // If we don't intercept them, the main page filters will handle them, causing:
  // 1. Full page reloads when applying filters in the modal
  // 2. Modal state being lost during page reloads
  // 3. Filters in the modal affecting the main page list unintentionally
  connect() {
    // Create a single bound handler for all filter events for better memory efficiency
    this.boundOnFilterChange = this.onFilterChange.bind(this)

    // Listen for all possible filter events - add new ones here if new filter types are created
    this.element.addEventListener("filters--checkbox-filter:filter", this.boundOnFilterChange)
    this.element.addEventListener("filters--text-filter:filter", this.boundOnFilterChange)
    this.element.addEventListener("filters--number-range-filter:filter", this.boundOnFilterChange)
    this.element.addEventListener("filters--integer-filter:filter", this.boundOnFilterChange)
    this.element.addEventListener("filters--radio-filter:filter", this.boundOnFilterChange)
    this.element.addEventListener("filters--date-filter:filter", this.boundOnFilterChange)
    this.element.addEventListener("filters--positions:filter", this.boundOnFilterChange)
  }

  // Clean up event listeners when the controller is disconnected
  disconnect() {
    this.element.removeEventListener("filters--checkbox-filter:filter", this.boundOnFilterChange)
    this.element.removeEventListener("filters--text-filter:filter", this.boundOnFilterChange)
    this.element.removeEventListener("filters--number-range-filter:filter", this.boundOnFilterChange)
    this.element.removeEventListener("filters--integer-filter:filter", this.boundOnFilterChange)
    this.element.removeEventListener("filters--radio-filter:filter", this.boundOnFilterChange)
    this.element.removeEventListener("filters--date-filter:filter", this.boundOnFilterChange)
    this.element.removeEventListener("filters--positions:filter", this.boundOnFilterChange)
  }

  onFilterChange(event) {
    // Only process events with matching context - this allows different contexts
    // to handle their own filtering logic independently
    if (event.detail && event.detail.context && event.detail.context !== this.contextValue) {
      return
    }

    // Critical: Stop propagation to prevent the main page controller from handling it
    // Without this, events would continue bubbling up to window-level handlers
    event.stopPropagation()

    // Find the audience controller to update the filters
    const audienceController = this.application.getControllerForElementAndIdentifier(
      document.querySelector("[data-controller~='campaign--audience']"),
      "campaign--audience"
    )

    if (audienceController) {
      if (event.detail.name && event.detail.values !== undefined) {
        // Update the filter value in the audience controller
        audienceController.filters[event.detail.name] = event.detail.values

        // Update the sample display
        audienceController.displayLeadsSample()
      }
    }
  }
}
